import type { MeResponse } from '@tabel/shared/schemas/auth';
import { useQueryClient } from '@tanstack/react-query';
import { FileCheck2 } from 'lucide-react';
import { useId } from 'react';
import { ruDate, ruDateTime } from '../../lib/format';
import { Badge, Button } from '../ui';
import { CardHead } from './CardHead';
import { CARD_TEXTS } from './texts-cards';
import type { ProjectCardProps, ProjectRow } from './types';

/*
 * Карточка «Проект новой версии правил №N готов» в ленте (006, T089; US4-5; FR-033, FR-034; contracts/ui.md §2; мокап
 * Main.dc.html, PhoneProject.dc.html).
 * - Состояние — с сервера (`state` строки сверяет сервер с проектом 005 при открытии разговора, `stateText` — тот же
 *   текст, что на странице правил): «Готов» (акцент), «Опубликован» (успех) и его текст, «Устарел» (внимание) —
 *   заголовок «Проект №N устарел» и причина (FR-033).
 * - У действующего проекта: с какой даты новые правила, краткий состав, что нужно перед публикацией (флажки, `needs`
 *   сервера) и до какого времени можно опубликовать — по времени объекта.
 * - «Открыть проект» — та же правая панель в режиме «проект» (`onOpen`, FR-034). Кнопки «Опубликовать» в чате нет:
 *   публикует человек на странице правил.
 */

const { label, text, say } = CARD_TEXTS;

type State = ProjectRow['state'];
const BADGE: Record<State, { tone: 'accent' | 'ok' | 'warn'; text: string }> = {
  active: { tone: 'accent', text: label.projectReady },
  published: { tone: 'ok', text: label.projectPublished },
  stale: { tone: 'warn', text: label.projectStale },
  expired: { tone: 'warn', text: label.projectStale },
};

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const sentence = (s: string) => (/[.!?…]$/u.test(s.trim()) ? s.trim() : `${s.trim()}.`);

/**
 * Часовой пояс объекта — из профиля, который уже загрузила оболочка приложения (`['me', роль]`, `App.tsx`): карточка
 * своего запроса не делает. Профиля нет (карточка вне оболочки) — пояс по умолчанию `lib/format`.
 */
function useObjectTz(): string | undefined {
  const qc = useQueryClient();
  return qc.getQueriesData<MeResponse>({ queryKey: ['me'] }).find(([, d]) => d)?.[1]?.object.tzOffset;
}

function title(row: ProjectRow): string {
  if (row.state === 'published') return say.projectPublished(row.planId);
  if (row.state === 'stale' || row.state === 'expired') return say.projectStale(row.planId);
  return say.projectReady(row.planId);
}

export function ProjectCard({ row, onOpen }: ProjectCardProps) {
  const titleId = useId();
  const tz = useObjectTz();
  const active = row.state === 'active';
  const badge = BADGE[row.state];
  return (
    <article
      aria-labelledby={titleId}
      className="flex flex-col gap-2 rounded-card border border-line bg-surface px-4 py-3 shadow-card"
    >
      <CardHead
        icon={FileCheck2}
        id={titleId}
        title={title(row)}
        badge={
          <Badge tone={badge.tone} dot>
            {badge.text}
          </Badge>
        }
      />
      {!active && row.stateText && <p className="text-[13px] text-ink">{sentence(row.stateText)}</p>}
      {row.validFrom && <p className="text-[13px] text-ink">{say.projectValidFrom(ruDate(row.validFrom))}</p>}
      {row.changes.length > 0 && (
        <ul aria-label={label.projectChanges} className="list-disc pl-[18px] text-[13px] text-ink">
          {row.changes.map((c) => (
            <li key={c} className="break-words">
              {c}
            </li>
          ))}
        </ul>
      )}
      <p className="text-[13px] text-ink-2">
        {active && row.needs.length > 0 && <>{say.projectNeeds(row.needs.map(lowerFirst).join('; '))} </>}
        {active && row.expiresAt && <>{say.projectUntil(ruDateTime(row.expiresAt, tz))} </>}
        {text.projectWho}
      </p>
      <div>
        <Button size="sm" className="max-sm:min-h-10" onClick={() => onOpen(row.planId)}>
          {label.openProject}
        </Button>
      </div>
    </article>
  );
}
